import React from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import SEO from "@/components/SEO";

/**
 * Terms & Conditions — real, brand-specific content covering the storefront,
 * checkout via Razorpay, product accuracy, pricing, shipping, returns/exchanges,
 * and dispute handling. Written for a real customer + Razorpay's KYC review.
 */
export default function Terms() {
  return (
    <main className="mx-auto max-w-3xl px-5 sm:px-10 py-14 sm:py-20" data-testid="terms-page">
      <SEO
        title="Terms & Conditions"
        description="Star Jewels Terms & Conditions — the rules of use for shopping artificial fashion jewellery on our website, including order acceptance, pricing, payment, delivery, exchanges and dispute resolution."
        path="/terms"
      />
      <div className="overline text-center">Legal</div>
      <h1 className="font-display text-4xl sm:text-5xl font-light text-center mt-2 leading-tight">Terms &amp; Conditions</h1>
      <p className="text-sm text-sj-muted text-center mt-3">Last updated: 6 February 2026</p>

      <div className="mt-10 space-y-8 text-sj-inkSoft leading-relaxed">
        <section>
          <p className="p-5 bg-sj-cream rounded-md flex items-start gap-3 not-italic">
            <FileText size={18} className="text-sj-goldDark mt-0.5 shrink-0" />
            <span>
              These Terms &amp; Conditions ("Terms") govern your access to and use of the Star Jewels
              website (<strong>shopstarjewels.com</strong>) and the purchase of any product offered on it.
              By browsing, placing an order, or creating an account you agree to be bound by these Terms.
              If you do not agree, please refrain from using the site.
            </span>
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">1. About Star Jewels</h2>
          <p className="mt-2">
            Star Jewels is a small-batch fashion &amp; imitation-jewellery brand based in India.
            Every piece is artificial (fashion) jewellery — <strong>not</strong> precious gold, silver
            or real gemstones — unless clearly labelled otherwise on the product page.
            Contact: <strong>officialstarjewels@gmail.com</strong> · Instagram
            <a href="https://instagram.com/officialstarjewels" target="_blank" rel="noopener noreferrer" className="text-sj-goldDark underline underline-offset-4 ml-1">@officialstarjewels</a>.
          </p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">2. Eligibility &amp; account</h2>
          <p className="mt-2">You must be at least 18 years old (or have a parent/guardian's consent) to place an order.
          You are responsible for keeping your account credentials confidential and for every action taken
          under your account. Please give us accurate name, address, email and phone details so we can
          deliver your order.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">3. Products &amp; catalogue accuracy</h2>
          <p className="mt-2">We photograph and describe every piece as faithfully as possible. However, colour reproduction
          varies across screens, and hand-crafted items may show minor natural variations from the photograph.
          Metal-tone terms such as "gold" or "rose-gold" refer to the <em>plating tone</em>, not to precious metal
          content. Any weights, dimensions or stone details on a product page are approximate.</p>
          <p className="mt-2">Product availability, prices, and offers can change at any time until an order is placed.
          Every listing includes stock information; if an ordered item unexpectedly becomes unavailable, we will
          contact you and issue a full refund.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">4. Pricing &amp; taxes</h2>
          <p className="mt-2">All prices are shown in Indian Rupees (₹) and are inclusive of applicable GST unless stated
          otherwise. Where an MRP is displayed with a strikethrough, the selling price is our current
          discounted price. Shipping is free on orders above ₹1,499 within India; otherwise a nominal
          shipping fee is added at checkout and shown clearly before you pay.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">5. Order acceptance</h2>
          <p className="mt-2">Your order is an <strong>offer to buy</strong>. We reserve the right to accept or decline it
          at our sole discretion (for example, pricing errors, suspected fraud, or stock unavailability).
          A binding contract is formed only when we send an order-confirmation email. Star Jewels may
          cancel an order and refund your payment in full if we are unable to fulfil it.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">6. Payments</h2>
          <p className="mt-2">Online payments are processed by <strong>Razorpay</strong>, a PCI-DSS-compliant payment gateway.
          We accept UPI, credit/debit cards, net-banking and popular wallets supported by Razorpay.
          We do <strong>not</strong> store your card or UPI credentials on our servers — they are handled entirely by
          Razorpay under their <a href="https://razorpay.com/terms/" target="_blank" rel="noopener noreferrer" className="text-sj-goldDark underline underline-offset-4">Terms of Service</a>.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">7. Shipping &amp; delivery</h2>
          <p className="mt-2">We ship across India via reputable courier partners. Orders are dispatched within
          2–4 business days of confirmation. Estimated delivery is 4–8 business days depending on your
          location. You will receive an email/SMS with a tracking link once your parcel is dispatched.
          Star Jewels is not responsible for courier delays caused by weather, local disruptions,
          or incorrect delivery information provided at checkout.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">8. Returns &amp; exchanges</h2>
          <p className="mt-2">Because we personally inspect every piece before dispatch (our 5-point quality check covers
          stone setting, plating &amp; finish, clasp strength, polish, and final packaging), we do <strong>not accept
          general returns</strong>. Exchanges are offered <strong>only if the piece arrives damaged or defective</strong>, and
          must be reported <strong>within 48 hours of delivery</strong> to <strong>officialstarjewels@gmail.com</strong> or via the on-site
          Moon chat, with 1–2 clear photos and your order reference. Full details:
          <Link to="/return-policy" className="text-sj-goldDark underline underline-offset-4 ml-1">Return &amp; Exchange Policy</Link>.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">9. Care &amp; product life</h2>
          <p className="mt-2">Fashion jewellery is designed for occasional wear. To keep your piece beautiful:
          store it dry, avoid contact with perfume, water, sweat or lotions, and wipe gently with a
          soft cloth after use. Plating and finish wear naturally over time — this is not a defect
          and is not covered by exchange.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">10. Intellectual property</h2>
          <p className="mt-2">All content on this site — photographs, product descriptions, illustrations, logos, and the
          Star Jewels name — is the property of Star Jewels. You may not reproduce, distribute, or use
          any of it for commercial purposes without our written permission.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">11. Acceptable use</h2>
          <p className="mt-2">You agree not to (a) misuse or attempt to disrupt the site, (b) place fraudulent orders,
          (c) resell products purchased at retail without our consent, or (d) upload harmful, unlawful,
          or infringing content to any part of the site (including chats or reviews). We may suspend
          or terminate accounts that breach these rules.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">12. Limitation of liability</h2>
          <p className="mt-2">To the fullest extent permitted by law, Star Jewels' total liability arising out of or in
          connection with any purchase is limited to the amount you paid for that order. We are not
          liable for indirect, incidental or consequential losses (including loss of profit or
          reputation) arising from your use of the site or a product.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">13. Governing law &amp; jurisdiction</h2>
          <p className="mt-2">These Terms are governed by the laws of India. Any dispute arising out of or in connection
          with your use of the site will be subject to the exclusive jurisdiction of the courts of
          India. We prefer to resolve concerns amicably — please write to us first at
          <strong> officialstarjewels@gmail.com</strong> and we will do our best to help.</p>
        </section>

        <section>
          <h2 className="font-display text-xl text-sj-ink">14. Changes to these Terms</h2>
          <p className="mt-2">We may update these Terms from time to time to reflect changes in our services,
          the law, or our operations. The current version is always available at
          <strong> shopstarjewels.com/terms</strong>. Continued use of the site after an update means you accept the
          revised Terms.</p>
        </section>

        <div className="pt-4 flex flex-wrap gap-4 text-sm">
          <Link to="/privacy" className="text-sj-goldDark underline underline-offset-4">Privacy Policy</Link>
          <Link to="/return-policy" className="text-sj-goldDark underline underline-offset-4">Return &amp; Exchange Policy</Link>
        </div>
      </div>
    </main>
  );
}
